import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { setSnackInfo } from "../../redux/actions/appAction";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Skeleton } from '@material-ui/lab';
import {
    Button,
    CircularProgress,
    LinearProgress,
    Grid,
    Paper,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip as MuiTooltip,
    Dialog,
    DialogContent,
    DialogActions,
    TablePagination,
    makeStyles,
} from '@material-ui/core';
import BusinessIcon from '@material-ui/icons/Business';
import PeopleAltOutlinedIcon from '@material-ui/icons/PeopleAltOutlined';
import EmojiEventsOutlinedIcon from '@material-ui/icons/EmojiEventsOutlined';
import AccountBalanceWalletOutlinedIcon from '@material-ui/icons/AccountBalanceWalletOutlined';
import StorefrontIcon from '@material-ui/icons/Storefront';
import GetAppIcon from '@material-ui/icons/GetApp';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import TuneIcon from '@material-ui/icons/Tune';
import InboxOutlinedIcon from '@material-ui/icons/InboxOutlined';
import HttpService from '../../services/httpService';

const NAVY = '#1C375D';
const BLUE = '#2B5AA7';
const LINK_BLUE = '#1976d2';
const ORANGE = '#F47E46';
const GREEN = '#2E7D32';
const PURPLE = '#7c5cd6';
const HEADER_GRADIENT = `linear-gradient(135deg, ${NAVY} 0%, ${BLUE} 70%, #4a7fd1 100%)`;

const GRAND_TOTAL = 'grand total';
const isGrandTotalRow = (row) => String(row?.channelPartner || '').trim().toLowerCase() === GRAND_TOTAL;

// The eleven count columns are identical apart from which field they read and
// which stage label the drill-down modal is opened with.
const STAGE_COLUMNS = [
    { key: 'leadCreated', idsKey: 'leadCreatedIds', label: 'Lead Created', stage: 'Lead Created' },
    { key: 'm2m', idsKey: 'm2mIds', label: 'M2M', stage: 'M2M' },
    { key: 'real', idsKey: 'realIds', label: 'Real', stage: 'Real' },
    { key: 'meetingDone', idsKey: 'meetingDoneIds', label: 'Meeting Done', stage: 'Meeting Done' },
    { key: 'salesWon', idsKey: 'salesWonIds', label: 'Sales Won', stage: 'Sales Won' },
    { key: 'junk', idsKey: 'junkIds', label: 'Junk', stage: 'Junk' },
    { key: 'toBeCalled', idsKey: 'toBeCalledIds', label: 'To Be Called', stage: 'To Be Called' },
    { key: 'botWaFollowUp', idsKey: 'botWaFollowUpIds', label: 'Bot WA Follow Up', stage: 'Bot Wa Follow Up' },
    { key: 'ntbcLeads', idsKey: 'ntbcLeadsIds', label: 'NTBC Leads', stage: 'NTBC Leads' },
    { key: 'hold', idsKey: 'holdIds', label: 'Hold', stage: 'Hold' },
    { key: 'salesLost', idsKey: 'salesLostIds', label: 'Sales Lost', stage: 'Sales Lost' },
];

function formatCurrency(value) {
    if (value == null || value === '') return '-';
    const number = Number(value);
    if (Number.isNaN(number) || !isFinite(number)) return '-';

    if (number >= 10000000) return `₹${(number / 10000000).toFixed(2)} Cr`;

    try {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(number);
    } catch (err) {
        return `₹${Math.round(number)}`;
    }
}

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '-');
const formatCount = (value) => Number(value || 0).toLocaleString('en-IN');

const MODAL_COLUMNS = [
    { label: 'Project No', get: (l) => l?.erpProjectNo || 'N/A' },
    { label: 'Lead No', key: 'leadNo' },
    { label: 'Customer', get: (l) => l.customer?.name || 'N/A', strong: true },
    { label: 'Contact', get: (l) => l.customer?.contact_no || 'N/A', strong: true },
    { label: 'Email', get: (l) => l.customer?.email || 'N/A' },
    { label: 'Created At', get: (l) => formatDateTime(l.createdAt) },
    { label: 'Lead Assigned', get: (l) => formatDateTime(l.leadAssignedDate) },
    { label: 'Source', get: (l) => l.sourceOfLead?.name || 'N/A' },
    { label: 'Sales Executive', get: (l) => l.salesUser?.name || 'N/A' },
    { label: 'Sales Assistant Manager', get: (l) => l.salesAssistantManager?.name || 'N/A' },
    { label: 'Sales Manager', get: (l) => l.salesManager?.name || 'N/A' },
    { label: 'Sales AGM', get: (l) => l.salesAGM?.name || 'N/A' },
    { label: 'Sales DGM', get: (l) => l.salesDGM?.name || 'N/A' },
    { label: 'Value', get: (l) => formatCurrency(l.staticSalesWonAmount), strong: true },
    { label: 'City', get: (l) => l.city?.name || 'N/A' },
    { label: 'Experience Center', get: (l) => l.experienceCenter?.name || 'N/A' },
    { label: 'Sales Won Date', get: (l) => formatDateTime(l.leadWonDate) },
    { label: 'Design KOM Internal Date', get: (l) => formatDateTime(l.designKickOffInternalApprovalDate) },
    { label: 'Design Sign-Off Date', get: (l) => formatDateTime(l.customerDesignSignOffDate) },
    { label: 'Token %', get: (l) => l.tokenPercent ?? '-' },
    { label: 'Current Stage', get: (l) => l.currentStage || '-', strong: true },
];

const useStyles = makeStyles(() => ({
    pageRoot: {
        background: 'linear-gradient(180deg, #F7F9FC 0%, #EFF3F9 100%)',
        minHeight: '100vh',
        padding: '14px 18px 32px',
    },
    banner: {
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        flexWrap: 'wrap',
        padding: '20px 26px',
        borderRadius: 18,
        marginBottom: 16,
        background: HEADER_GRADIENT,
        boxShadow: '0 12px 30px rgba(28,55,93,0.28)',
        '&::after': {
            content: '""',
            position: 'absolute',
            top: -80,
            right: -30,
            width: 260,
            height: 260,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 70%)',
            pointerEvents: 'none',
        },
        '& > *': { position: 'relative', zIndex: 1 },
    },
    bannerIcon: {
        width: 50,
        height: 50,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        background: 'rgba(255,255,255,0.16)',
        border: '2px solid rgba(255,255,255,0.3)',
        color: '#fff',
    },
    bannerTitle: { fontSize: 20, fontWeight: 700, color: '#fff', letterSpacing: 0.2 },
    bannerSubtitle: { fontSize: 13, color: 'rgba(255,255,255,0.78)', marginTop: 4 },
    csvBtn: {
        background: `linear-gradient(135deg, #1b5e20, ${GREEN})`,
        color: '#fff',
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 10,
        padding: '8px 20px',
        boxShadow: '0 4px 12px rgba(46,125,50,0.3)',
        '&:hover': { background: 'linear-gradient(135deg, #154a19, #256428)' },
        '&.Mui-disabled': { background: 'rgba(255,255,255,0.22)', color: 'rgba(255,255,255,0.65)', boxShadow: 'none' },
    },

    filterCard: {
        padding: '16px 20px',
        marginBottom: 16,
        borderRadius: 16,
        border: '1px solid #eaeff6',
        boxShadow: '0 2px 16px rgba(28,55,93,0.06)',
    },
    filterTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        color: NAVY,
        fontWeight: 700,
        fontSize: 13,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: 14,
    },
    field: {
        '& .MuiOutlinedInput-root': { borderRadius: 10 },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BLUE },
        '& .MuiInputLabel-root.Mui-focused': { color: NAVY },
    },
    searchBtn: {
        background: `linear-gradient(135deg, #d9622f, ${ORANGE})`,
        color: '#fff',
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 10,
        height: 40,
        boxShadow: '0 4px 12px rgba(244,126,70,0.3)',
        '&:hover': { background: 'linear-gradient(135deg, #c25527, #e26f38)' },
        '&.Mui-disabled': { background: '#f0cbb8', color: '#fff', boxShadow: 'none' },
    },
    clearBtn: {
        background: '#fff',
        color: '#5c6779',
        border: '1px solid #dde4ee',
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 10,
        height: 40,
        '&:hover': { background: '#f5f7fb' },
    },

    statCard: {
        height: '100%',
        borderRadius: 16,
        border: '1px solid #eaeff6',
        boxShadow: '0 2px 16px rgba(28,55,93,0.06)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 26px rgba(28,55,93,0.12)' },
    },
    statInner: { display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px !important' },
    statIcon: {
        width: 46,
        height: 46,
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    statLabel: {
        fontSize: 11.5,
        fontWeight: 700,
        color: '#8b93a6',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },
    statValue: { fontSize: 23, fontWeight: 700, color: '#1f2d3d', marginTop: 3, lineHeight: 1.15 },
    statSub: { fontSize: 11.5, color: '#9aa4b4', marginTop: 3 },

    tableCard: {
        borderRadius: 16,
        border: '1px solid #eaeff6',
        boxShadow: '0 2px 16px rgba(28,55,93,0.06)',
        overflow: 'hidden',
    },
    tableToolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        padding: '16px 18px 14px',
    },
    tableTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        fontSize: 15,
        fontWeight: 700,
        color: '#1f2d3d',
        '&::before': {
            content: '""',
            width: 4,
            height: 16,
            borderRadius: 3,
            background: `linear-gradient(180deg, ${BLUE}, ${NAVY})`,
        },
    },
    tableSubtitle: { fontSize: 12, color: '#8b93a6', marginTop: 3, paddingLeft: 13 },
    searchField: {
        '& .MuiOutlinedInput-root': { borderRadius: 999, backgroundColor: '#fff' },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BLUE },
    },
    tableWrap: { maxHeight: '68vh', overflow: 'auto' },
    headCell: {
        position: 'sticky',
        top: 0,
        zIndex: 3,
        background: `linear-gradient(135deg, ${NAVY} 0%, #24446f 100%)`,
        color: '#ffffff',
        fontSize: 11.5,
        fontWeight: 600,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        border: 'none',
        padding: '13px 12px',
    },
    bodyCell: {
        borderBottom: '1px solid #edf0f5',
        padding: '10px 12px',
        fontSize: 13.5,
        color: '#41506b',
        textAlign: 'center',
        whiteSpace: 'nowrap',
    },
    bodyRow: {
        transition: 'background-color 0.15s ease',
        '&:nth-of-type(even)': { backgroundColor: '#FBFCFE' },
        '&:hover': { backgroundColor: '#F1F6FD' },
    },
    totalRow: {
        background: '#EEF3FB !important',
        '& td': {
            fontWeight: 700,
            color: NAVY,
            borderTop: `2px solid ${BLUE}`,
            fontSize: 14,
        },
    },
    partnerCell: { textAlign: 'left', fontWeight: 600, color: '#2b3648', whiteSpace: 'normal', minWidth: 190 },
    countPill: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 44,
        padding: '4px 10px',
        borderRadius: 8,
        background: '#EAF2FD',
        color: LINK_BLUE,
        fontWeight: 700,
        fontSize: 13,
        cursor: 'pointer',
        border: '1px solid transparent',
        transition: 'all 0.15s ease',
        '&:hover': { background: '#d7e6fa', borderColor: `${LINK_BLUE}44` },
    },
    countZero: { color: '#b6bdc9', fontWeight: 600 },
    valueCell: { fontWeight: 700, color: NAVY, whiteSpace: 'nowrap' },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '44px 20px',
        color: '#9aa4b4',
        textAlign: 'center',
    },
    emptyIcon: { fontSize: 36, color: '#c7cedb', marginBottom: 8 },

    dialogHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        flexWrap: 'wrap',
        padding: '16px 22px',
        background: HEADER_GRADIENT,
        color: '#fff',
    },
    dialogTitle: { fontSize: 17, fontWeight: 700 },
    dialogSubtitle: { fontSize: 12.5, color: 'rgba(255,255,255,0.78)', marginTop: 3 },
    modalHeadCell: {
        position: 'sticky',
        top: 0,
        zIndex: 3,
        background: `linear-gradient(135deg, ${NAVY} 0%, #24446f 100%)`,
        color: '#fff',
        fontWeight: 600,
        fontSize: 11,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        border: 'none',
        padding: '11px 12px',
    },
    modalBodyCell: {
        borderBottom: '1px solid #e4e9f2',
        padding: '10px 12px',
        fontSize: 13,
        color: '#1d2a3d',
        whiteSpace: 'nowrap',
    },
    // Columns people actually scan for - pushed darker/heavier than the rest.
    modalStrongCell: { fontWeight: 600, color: '#0f1a2b' },
    modalRow: {
        transition: 'background-color 0.15s ease',
        '&:nth-of-type(even)': { backgroundColor: '#F4F8FE' },
        '&:hover': { backgroundColor: '#E6F0FD' },
    },
    modalIndexCell: { color: '#6b7689', fontWeight: 600 },
    leadLink: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 9px',
        borderRadius: 6,
        background: '#EAF2FD',
        color: NAVY,
        fontWeight: 700,
        textDecoration: 'none',
        '&:hover': { background: '#d7e6fa' },
    },
    closeBtn: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 9,
        background: '#eef1f6',
        color: '#41506b',
        '&:hover': { background: '#e2e7f0' },
    },
}));

const StatCard = ({ label, value, sub, accent, icon, loading, classes }) => (
    <Card className={classes.statCard}>
        <CardContent className={classes.statInner}>
            <div className={classes.statIcon} style={{ backgroundColor: `${accent}1a`, color: accent }}>
                {icon}
            </div>
            <div style={{ minWidth: 0 }}>
                <div className={classes.statLabel}>{label}</div>
                {loading ? (
                    <Skeleton variant="text" width={100} height={30} />
                ) : (
                    <>
                        <div className={classes.statValue}>{value}</div>
                        {sub ? <div className={classes.statSub}>{sub}</div> : null}
                    </>
                )}
            </div>
        </CardContent>
    </Card>
);

const ChannelPartnerPerformance = (props) => {
    const classes = useStyles();
    const { setSnackInfo } = props;

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [filter, setFilter] = useState('');
    const [storeDateFilter, setPivotDateFilters] = useState({ startDate: '', endDate: '' });
    const [sources, setSources] = useState([]);
    const [selectedSourceOfLead, setSelectedSourceOfLead] = useState([]);

    const [isExporting, setIsExporting] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalLeads, setModalLeads] = useState([]);
    const rowsPerPage = 100;

    const [modalData, setModalData] = useState({
        leadIds: [],
        partnerName: '',
        stageName: '',
        page: 0,
        total: 0,
    });

    const fetchData = useCallback(async (addSources, filters = storeDateFilter) => {
        setLoading(true);
        try {
            const sourcesData = addSources?.map((el) => el.name) || selectedSourceOfLead?.map((el) => el.name);
            const res = await HttpService.getChannelPartnerPerformance({
                startDate: filters.startDate,
                endDate: filters.endDate,
                selectedSources: sourcesData ? sourcesData : '',
            });
            setData(res.data.data || []);
        } catch (error) {
            setSnackInfo(error?.response?.data?.message ?? 'Something went wrong', 'error');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSourceOfLead, storeDateFilter, setSnackInfo]);

    const getSourceOfLeads = useCallback(async () => {
        try {
            const response = await HttpService.getLeadMaster();
            setSources((response.data || []).filter((item) => item.sourceType === 'Channel Partner'));
        } catch (error) {
            console.error('Error fetching source of leads:', error?.response?.data || error.message);
        }
    }, []);

    useEffect(() => {
        fetchData();
        getSourceOfLeads();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchPartnerLeads = async (leadIds = [], partnerName, stageName, pageNumber = 1) => {
        setModalOpen(true);
        setModalLoading(true);
        try {
            const response = await HttpService.getChannelPartnerLeadDetails({
                leadIds,
                page: pageNumber,
                limit: rowsPerPage,
            });
            setModalLeads(response.data.data || []);
            setModalData({
                leadIds,
                partnerName,
                stageName,
                page: pageNumber - 1,
                total: response.data.totalCount,
            });
        } catch (error) {
            console.error(error);
            setSnackInfo(error?.response?.data?.message || 'Failed to load partner leads', 'error');
        } finally {
            setModalLoading(false);
        }
    };

    const filteredData = useMemo(() => {
        const normalized = filter.trim().toLowerCase();
        if (!normalized) return data;
        return data.filter((row) =>
            String(row.channelPartner || '').toLowerCase().includes(normalized),
        );
    }, [data, filter]);

    const partnerRows = useMemo(() => filteredData.filter((row) => !isGrandTotalRow(row)), [filteredData]);
    const grandTotalRow = useMemo(() => filteredData.find(isGrandTotalRow), [filteredData]);

    // Totals come from the partner rows only - the backend's own Grand Total row
    // is excluded so it cannot be counted twice.
    const summary = useMemo(
        () =>
            partnerRows.reduce(
                (acc, row) => {
                    acc.leadsCreated += Number(row.leadCreated || 0);
                    acc.salesWon += Number(row.salesWon || 0);
                    acc.totalValue += Number(row.salesValue || 0);
                    return acc;
                },
                { leadsCreated: 0, salesWon: 0, totalValue: 0 },
            ),
        [partnerRows],
    );

    const conversionRate = summary.leadsCreated > 0
        ? ((summary.salesWon / summary.leadsCreated) * 100).toFixed(1)
        : null;

    const handlePivotDateFilter = () => {
        const { startDate, endDate } = storeDateFilter;
        if (!startDate || !endDate) {
            setSnackInfo('Please select both start and end date', 'warning');
            return;
        }

        const diffInDays = (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24);
        if (diffInDays > 31) {
            setSnackInfo('Data fetching range is too large. Please select a date range within 1 month.', 'warning');
            return;
        }

        fetchData(selectedSourceOfLead, storeDateFilter);
    };

    const clearDateFilter = () => {
        const clearedFilters = { startDate: '', endDate: '' };
        setPivotDateFilters(clearedFilters);
        fetchData(selectedSourceOfLead, clearedFilters);
    };

    const storePivotDate = (e) => {
        const { name, value } = e.target;
        setPivotDateFilters({ ...storeDateFilter, [name]: value });
    };

    const handleMultipleSourceOfLead = (event, newValue) => {
        setSelectedSourceOfLead(newValue);
        fetchData(newValue, storeDateFilter);
    };

    const downloadCsvFile = (headers, rows, filename) => {
        const csvContent = [headers, ...rows]
            .map((line) => line.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const downloadCsv = () => {
        if (!filteredData.length) return;

        const headers = ['Channel Partner', ...STAGE_COLUMNS.map((c) => c.label), 'Sales Value'];
        const rows = filteredData.map((row) => [
            row.channelPartner || '-',
            ...STAGE_COLUMNS.map((c) => row[c.key] ?? 0),
            row.salesValueFormatted || formatCurrency(row.salesValue),
        ]);

        downloadCsvFile(headers, rows, `channel-partner-summary-${new Date().toISOString().slice(0, 10)}.csv`);
    };

    const downloadModalCsv = async () => {
        try {
            setIsExporting(true);
            const response = await HttpService.getChannelPartnerLeadDetails({
                leadIds: modalData.leadIds,
                page: 1,
                limit: modalData.leadIds.length, // fetch all leads
            });

            const allLeads = response.data.data || [];
            if (!allLeads.length) {
                setSnackInfo('No leads available to export', 'warning');
                return;
            }

            const headers = ['Sl. No.', ...MODAL_COLUMNS.map((c) => c.label)];
            const rows = allLeads.map((l, index) => [
                index + 1,
                ...MODAL_COLUMNS.map((c) => (c.key === 'leadNo' ? l.lead_no || '-' : c.get(l))),
            ]);

            downloadCsvFile(headers, rows, `${modalData.partnerName}-${modalData.stageName}.csv`);
        } catch (error) {
            console.error('CSV Export Error:', error);
            setSnackInfo('Failed to export leads', 'error');
        } finally {
            setIsExporting(false);
        }
    };

    const renderSkeletonRows = () =>
        Array.from({ length: 7 }).map((_, i) => (
            <TableRow key={`sk-${i}`}>
                <TableCell className={classes.bodyCell}><Skeleton variant="text" width={20} style={{ margin: '0 auto' }} /></TableCell>
                <TableCell className={`${classes.bodyCell} ${classes.partnerCell}`}><Skeleton variant="text" width="70%" /></TableCell>
                {STAGE_COLUMNS.map((c) => (
                    <TableCell key={c.key} className={classes.bodyCell}>
                        <Skeleton variant="rect" width={44} height={26} style={{ borderRadius: 8, margin: '0 auto' }} />
                    </TableCell>
                ))}
                <TableCell className={classes.bodyCell}><Skeleton variant="text" width="70%" style={{ margin: '0 auto' }} /></TableCell>
            </TableRow>
        ));

    const renderCountCell = (row, column) => {
        const count = row[column.key] ?? 0;
        if (!count) return <span className={classes.countZero}>0</span>;
        return (
            <MuiTooltip title={`View ${column.label} leads for ${row.channelPartner}`}>
                <span
                    className={classes.countPill}
                    onClick={() => fetchPartnerLeads(row[column.idsKey], row.channelPartner, column.stage, 1)}
                >
                    {formatCount(count)}
                </span>
            </MuiTooltip>
        );
    };

    const renderDataRow = (row, index, isTotal) => (
        <TableRow
            key={`${row.channelPartner}-${index}`}
            className={isTotal ? classes.totalRow : classes.bodyRow}
        >
            <TableCell className={classes.bodyCell} style={{ color: '#9aa4b4' }}>
                {isTotal ? '—' : index + 1}
            </TableCell>
            <TableCell className={`${classes.bodyCell} ${classes.partnerCell}`}>
                {row.channelPartner || '-'}
            </TableCell>
            {STAGE_COLUMNS.map((column) => (
                <TableCell key={column.key} className={classes.bodyCell}>
                    {renderCountCell(row, column)}
                </TableCell>
            ))}
            <TableCell className={`${classes.bodyCell} ${classes.valueCell}`}>
                {row.salesValueFormatted || formatCurrency(row.salesValue)}
            </TableCell>
        </TableRow>
    );

    return (
        <div className={classes.pageRoot}>
            <div className={classes.banner}>
                <div className={classes.bannerIcon}>
                    <BusinessIcon style={{ fontSize: 26 }} />
                </div>
                <div style={{ flex: 1, minWidth: 220 }}>
                    <div className={classes.bannerTitle}>Channel Partner Dashboard</div>
                    <div className={classes.bannerSubtitle}>
                        Review channel partner performance, monitor lead activity and track sales value in one place.
                    </div>
                </div>
                <Button
                    variant="contained"
                    className={classes.csvBtn}
                    startIcon={<GetAppIcon />}
                    onClick={downloadCsv}
                    disabled={loading || !filteredData.length}
                >
                    Download CSV
                </Button>
            </div>

            <Paper className={classes.filterCard}>
                <div className={classes.filterTitle}>
                    <TuneIcon fontSize="small" />
                    Filters
                </div>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <Autocomplete
                            multiple
                            size="small"
                            value={selectedSourceOfLead}
                            onChange={handleMultipleSourceOfLead}
                            options={sources}
                            disabled={loading}
                            getOptionLabel={(option) => option.name}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Source of Lead"
                                    variant="outlined"
                                    size="small"
                                    className={classes.field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            type="date"
                            label="Created From"
                            name="startDate"
                            className={classes.field}
                            value={storeDateFilter.startDate}
                            onChange={storePivotDate}
                            disabled={loading}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            type="date"
                            label="Created To"
                            name="endDate"
                            className={classes.field}
                            value={storeDateFilter.endDate}
                            onChange={storePivotDate}
                            disabled={loading}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <Button
                            fullWidth
                            variant="contained"
                            className={classes.searchBtn}
                            onClick={handlePivotDateFilter}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SearchIcon />}
                        >
                            {loading ? 'Loading…' : 'Search'}
                        </Button>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <Button
                            fullWidth
                            variant="contained"
                            className={classes.clearBtn}
                            onClick={clearDateFilter}
                            disabled={loading}
                            startIcon={<ClearIcon />}
                        >
                            Clear
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={2} style={{ marginBottom: 16 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        classes={classes}
                        loading={loading}
                        label="Channel Partners"
                        value={formatCount(partnerRows.length)}
                        sub={filter ? 'matching search' : 'reporting activity'}
                        accent={BLUE}
                        icon={<StorefrontIcon />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        classes={classes}
                        loading={loading}
                        label="Leads Created"
                        value={formatCount(summary.leadsCreated)}
                        sub="across all partners"
                        accent={PURPLE}
                        icon={<PeopleAltOutlinedIcon />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        classes={classes}
                        loading={loading}
                        label="Sales Won"
                        value={formatCount(summary.salesWon)}
                        sub={conversionRate ? `${conversionRate}% of leads created` : 'no leads yet'}
                        accent={GREEN}
                        icon={<EmojiEventsOutlinedIcon />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        classes={classes}
                        loading={loading}
                        label="Sales Value"
                        value={formatCurrency(summary.totalValue)}
                        sub="total won value"
                        accent={ORANGE}
                        icon={<AccountBalanceWalletOutlinedIcon />}
                    />
                </Grid>
            </Grid>

            <Card className={classes.tableCard}>
                {loading && <LinearProgress />}
                <div className={classes.tableToolbar}>
                    <div>
                        <div className={classes.tableTitle}>Partner-wise performance</div>
                        <div className={classes.tableSubtitle}>
                            {loading
                                ? 'Fetching partner performance…'
                                : `${partnerRows.length} ${partnerRows.length === 1 ? 'partner' : 'partners'} shown · click any count to view those leads`}
                        </div>
                    </div>
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Search channel partner…"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className={classes.searchField}
                        style={{ minWidth: 260 }}
                        disabled={loading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon style={{ fontSize: 18, color: '#9aa4b4' }} />
                                </InputAdornment>
                            ),
                            endAdornment: filter ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setFilter('')} style={{ padding: 4 }}>
                                        <ClearIcon style={{ fontSize: 15 }} />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </div>

                <TableContainer className={classes.tableWrap}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.headCell} style={{ width: 62 }}>SL No</TableCell>
                                <TableCell className={classes.headCell} style={{ textAlign: 'left' }}>Channel Partner</TableCell>
                                {STAGE_COLUMNS.map((c) => (
                                    <TableCell key={c.key} className={classes.headCell}>{c.label}</TableCell>
                                ))}
                                <TableCell className={classes.headCell}>Sales Won Value</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                renderSkeletonRows()
                            ) : filteredData.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={STAGE_COLUMNS.length + 3} style={{ padding: 0, borderBottom: 'none' }}>
                                        <div className={classes.emptyState}>
                                            <InboxOutlinedIcon className={classes.emptyIcon} />
                                            <div style={{ fontSize: 14, fontWeight: 600, color: '#6b7689' }}>
                                                {filter ? 'No partners match your search' : 'No channel partner records found'}
                                            </div>
                                            <div style={{ fontSize: 12.5, marginTop: 4 }}>
                                                {filter
                                                    ? 'Try a different partner name or clear the search.'
                                                    : 'Adjust the source or date filters and search again.'}
                                            </div>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {partnerRows.map((row, index) => renderDataRow(row, index, false))}
                                    {grandTotalRow && renderDataRow(grandTotalRow, 0, true)}
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            <Dialog
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                fullWidth
                maxWidth={false}
                PaperProps={{ style: { width: '98vw', maxWidth: '98vw', height: '90vh', borderRadius: 14, overflow: 'hidden' } }}
            >
                <div className={classes.dialogHeader}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                        <div className={classes.dialogTitle}>
                            {modalData.partnerName || 'Channel Partner'} — {modalData.stageName}
                        </div>
                        <div className={classes.dialogSubtitle}>
                            {modalLoading
                                ? 'Loading leads…'
                                : `${formatCount(modalData.total)} lead${modalData.total === 1 ? '' : 's'} · showing page ${modalData.page + 1}`}
                        </div>
                    </div>
                    <Button
                        variant="contained"
                        className={classes.csvBtn}
                        startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <GetAppIcon />}
                        onClick={downloadModalCsv}
                        disabled={isExporting || modalLoading || !modalLeads.length}
                    >
                        {isExporting ? 'Exporting…' : 'Download CSV'}
                    </Button>
                    <IconButton size="small" onClick={() => setModalOpen(false)} style={{ color: '#fff' }}>
                        <CloseIcon />
                    </IconButton>
                </div>

                <DialogContent dividers style={{ padding: 0 }}>
                    {modalLoading && <LinearProgress />}
                    <TableContainer style={{ maxHeight: '68vh', overflowX: 'auto' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classes.modalHeadCell}>#</TableCell>
                                    {MODAL_COLUMNS.map((c) => (
                                        <TableCell key={c.label} className={classes.modalHeadCell}>{c.label}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {modalLoading ? (
                                    Array.from({ length: 8 }).map((_, i) => (
                                        <TableRow key={`msk-${i}`}>
                                            <TableCell className={classes.modalBodyCell}><Skeleton variant="text" width={18} /></TableCell>
                                            {MODAL_COLUMNS.map((c) => (
                                                <TableCell key={c.label} className={classes.modalBodyCell}>
                                                    <Skeleton variant="text" width={90} />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : modalLeads.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={MODAL_COLUMNS.length + 1} style={{ padding: 0, borderBottom: 'none' }}>
                                            <div className={classes.emptyState}>
                                                <InboxOutlinedIcon className={classes.emptyIcon} />
                                                <div style={{ fontSize: 14, fontWeight: 600, color: '#6b7689' }}>
                                                    No leads found for this selection
                                                </div>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    modalLeads.map((l, index) => (
                                        <TableRow key={l._id || index} className={classes.modalRow}>
                                            <TableCell className={`${classes.modalBodyCell} ${classes.modalIndexCell}`}>
                                                {modalData.page * rowsPerPage + index + 1}
                                            </TableCell>
                                            {MODAL_COLUMNS.map((c) => (
                                                <TableCell
                                                    key={c.label}
                                                    className={`${classes.modalBodyCell} ${c.strong ? classes.modalStrongCell : ''}`}
                                                >
                                                    {c.key === 'leadNo' ? (
                                                        l?.lead_no ? (
                                                            <Link
                                                                to={`/project-logs/${l._id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className={classes.leadLink}
                                                            >
                                                                {l.lead_no}
                                                            </Link>
                                                        ) : (
                                                            'NA'
                                                        )
                                                    ) : (
                                                        c.get(l)
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>

                <DialogActions style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 16px', alignItems: 'center' }}>
                    <TablePagination
                        component="div"
                        count={modalData.total || 0}
                        page={modalData.page}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={[rowsPerPage]}
                        onRowsPerPageChange={() => { }}
                        onPageChange={(e, newPage) =>
                            fetchPartnerLeads(modalData.leadIds, modalData.partnerName, modalData.stageName, newPage + 1)
                        }
                    />
                    <Button
                        variant="contained"
                        className={classes.closeBtn}
                        startIcon={<CloseIcon />}
                        onClick={() => setModalOpen(false)}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default connect(null, { setSnackInfo })(ChannelPartnerPerformance);
